import React, { useState, useEffect, useCallback } from 'react';
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import './ModernToggleSwitch.css';
import './CompanyNotificationSettings.css';

// Times read as 24h in these languages; the others get 12h with AM / PM (as in the app).
const DAY_CLOCK_LANGUAGES = ['de', 'gsw', 'wae', 'fr', 'it'];

/** "From" / "Until" tile of the app's notification schedule; the select opens the time list. */
function TimeTile({ icon, label, value, options, onChange }) {
    return (
        <label className="company-notifications__tile">
            <span className="company-notifications__tile-head">
                <span className="material-symbols-outlined" aria-hidden="true">{icon}</span>
                <span className="company-notifications__tile-label">{label}</span>
            </span>
            <select
                className="company-notifications__tile-select"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

const DEFAULT_TIMES = {
    startTime: '09:00',
    endTime: '18:00'
};

const DAYS_OF_WEEK = [
    { key: 'mon', translationKey: 'monday' },
    { key: 'tue', translationKey: 'tuesday' },
    { key: 'wed', translationKey: 'wednesday' },
    { key: 'thu', translationKey: 'thursday' },
    { key: 'fri', translationKey: 'friday' },
    { key: 'sat', translationKey: 'saturday' },
    { key: 'sun', translationKey: 'sunday' }
];

function CompanyNotificationSettings({ companyId }) {
    const { strings, language, isGerm } = React.useContext(LanguageContext);
    const lang = String(language || (isGerm ? 'de' : 'en')).toLowerCase().split('-')[0];

    const [loading, setLoading] = useState(false);
    const [notificationType, setNotificationType] = useState('everyday');
    const [startTime, setStartTime] = useState(DEFAULT_TIMES.startTime);
    const [endTime, setEndTime] = useState(DEFAULT_TIMES.endTime);
    const [customPreferences, setCustomPreferences] = useState({});
    const [isInitialized, setIsInitialized] = useState(false);

    const timeOptions = React.useMemo(() => {
        const options = [];
        const dayClock = DAY_CLOCK_LANGUAGES.includes(lang);
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const label = dayClock
                    ? time
                    : new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                options.push({ value: time, label });
            }
        }
        return options;
    }, [lang]);

    const getDayLabel = useCallback((dayKey) => {
        const day = DAYS_OF_WEEK.find(d => d.key === dayKey);
        return day ? (strings[day.translationKey] || dayKey) : dayKey;
    }, [strings]);

    const validateTimeRange = useCallback((start, end) => {
        if (!start || !end) return false;
        const startDate = new Date(`2000-01-01T${start}`);
        const endDate = new Date(`2000-01-01T${end}`);
        return startDate < endDate;
    }, []);

    const validateCustomPreferences = useCallback(() => {
        for (const [day, preference] of Object.entries(customPreferences)) {
            if (preference && preference.startTime && preference.endTime) {
                if (!validateTimeRange(preference.startTime, preference.endTime)) {
                    return { valid: false, day };
                }
            }
        }
        return { valid: true };
    }, [customPreferences, validateTimeRange]);

    const getDefaultCustomPreferences = useCallback(() => {
        const defaults = {};
        DAYS_OF_WEEK.forEach(day => {
            defaults[day.key] = {
                startTime: DEFAULT_TIMES.startTime,
                endTime: DEFAULT_TIMES.endTime
            };
        });
        return defaults;
    }, []);

    const loadNotificationPreferences = useCallback(async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            const response = await callAPI(`/company/${companyId}/notificationPreference`, {}, 'GET');

            if (response?.data?.status && response.data.data) {
                const preferences = response.data.data;
                const preference = Array.isArray(preferences) ? preferences[0] : preferences;

                if (preference) {
                    if (preference.type === 'everyday' || preference.type === 'weekdays') {
                        setNotificationType(preference.type);
                        setStartTime(preference.startTime || DEFAULT_TIMES.startTime);
                        setEndTime(preference.endTime || DEFAULT_TIMES.endTime);
                        setCustomPreferences(getDefaultCustomPreferences());
                    } else if (preference.type === 'custom' && preference.customPreference) {
                        setNotificationType('custom');
                        setStartTime(DEFAULT_TIMES.startTime);
                        setEndTime(DEFAULT_TIMES.endTime);
                        const defaultPrefs = getDefaultCustomPreferences();
                        const mergedPrefs = { ...defaultPrefs, ...preference.customPreference };
                        setCustomPreferences(mergedPrefs);
                    } else {
                        setNotificationType('everyday');
                        setStartTime(DEFAULT_TIMES.startTime);
                        setEndTime(DEFAULT_TIMES.endTime);
                        setCustomPreferences(getDefaultCustomPreferences());
                    }
                } else {
                    setNotificationType('everyday');
                    setStartTime(DEFAULT_TIMES.startTime);
                    setEndTime(DEFAULT_TIMES.endTime);
                    setCustomPreferences(getDefaultCustomPreferences());
                }
            } else {
                setNotificationType('everyday');
                setStartTime(DEFAULT_TIMES.startTime);
                setEndTime(DEFAULT_TIMES.endTime);
                setCustomPreferences(getDefaultCustomPreferences());
            }
        } catch (error) {
            console.error('Error loading company notification preferences:', error);
            setNotificationType('everyday');
            setStartTime(DEFAULT_TIMES.startTime);
            setEndTime(DEFAULT_TIMES.endTime);
            setCustomPreferences(getDefaultCustomPreferences());
        } finally {
            setLoading(false);
            setIsInitialized(true);
        }
    }, [companyId, getDefaultCustomPreferences]);

    const saveNotificationPreferences = useCallback(async () => {
        if (!companyId) return;
        try {
            setLoading(true);

            if (notificationType === 'everyday' || notificationType === 'weekdays') {
                if (!validateTimeRange(startTime, endTime)) {
                    $crud.notify({
                        type: 'error',
                        message: strings.start_time_before_end_time
                    });
                    return;
                }
            } else if (notificationType === 'custom') {
                const validation = validateCustomPreferences();
                if (!validation.valid) {
                    const dayName = getDayLabel(validation.day);
                    $crud.notify({
                        type: 'error',
                        message: `${dayName}: ${strings.start_time_before_end_time}`
                    });
                    return;
                }
            }

            let payload;
            if (notificationType === 'everyday' || notificationType === 'weekdays') {
                payload = {
                    type: notificationType,
                    startTime: startTime,
                    endTime: endTime
                };
            } else {
                payload = {
                    type: 'custom',
                    customPreference: customPreferences
                };
            }

            const response = await callAPI(`/company/${companyId}/notificationPreference/addUpdate`, {}, 'POST', payload);

            if (response?.data?.status) {
                $crud.notify({
                    type: 'success',
                    message: strings.notification_settings_saved
                });
            } else {
                $crud.notify({
                    type: 'error',
                    message: response?.data?.message || strings.notification_settings_error
                });
            }
        } catch (error) {
            console.error('Error saving company notification preferences:', error);
            $crud.notify({
                type: 'error',
                message: strings.notification_settings_error
            });
        } finally {
            setLoading(false);
        }
    }, [companyId, notificationType, startTime, endTime, customPreferences, validateTimeRange, validateCustomPreferences, getDayLabel, strings]);

    const updateCustomPreference = useCallback((day, field, value) => {
        setCustomPreferences(prev => {
            const updated = {
                ...prev,
                [day]: {
                    ...prev[day],
                    [field]: value
                }
            };

            if (updated[day] && updated[day].startTime && updated[day].endTime) {
                if (!validateTimeRange(updated[day].startTime, updated[day].endTime)) {
                    setTimeout(() => {
                        $crud.notify({
                            type: 'warning',
                            message: `${getDayLabel(day)}: ${strings.start_time_before_end_time}`
                        });
                    }, 100);
                }
            }

            return updated;
        });
    }, [validateTimeRange, getDayLabel, strings]);

    const toggleDayEnabled = useCallback((day) => {
        setCustomPreferences(prev => ({
            ...prev,
            [day]: prev[day] ? null : {
                startTime: DEFAULT_TIMES.startTime,
                endTime: DEFAULT_TIMES.endTime
            }
        }));
    }, []);

    const handleTimeChange = useCallback((field, value) => {
        if (field === 'startTime') {
            setStartTime(value);
        } else if (field === 'endTime') {
            setEndTime(value);
        }

        if (field === 'startTime' && endTime) {
            if (!validateTimeRange(value, endTime)) {
                setTimeout(() => {
                    $crud.notify({
                        type: 'warning',
                        message: strings.start_time_before_end_time
                    });
                }, 100);
            }
        } else if (field === 'endTime' && startTime) {
            if (!validateTimeRange(startTime, value)) {
                setTimeout(() => {
                    $crud.notify({
                        type: 'warning',
                        message: strings.start_time_before_end_time
                    });
                }, 100);
            }
        }
    }, [startTime, endTime, validateTimeRange, strings]);

    useEffect(() => {
        if (!isInitialized && companyId) {
            loadNotificationPreferences();
        }
    }, [isInitialized, companyId, loadNotificationPreferences]);

    const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
    const ranged = notificationType === 'everyday' || notificationType === 'weekdays';
    const startLabel = tr('company_notification_start_time', 'Startzeit', 'Start time');
    const endLabel = tr('company_notification_end_time', 'Endzeit', 'End time');

    // First load: the page's own shape (description, the type row, the two time tiles), shimmering.
    if (!isInitialized) {
        return (
            <div className="company-notifications" role="status" aria-busy="true" aria-label={`${strings.loading || 'Loading'}...`}>
                <div className="company-notifications__bones">
                    <span className="company-notifications__bone company-notifications__bone--wide" />
                    <span className="company-notifications__bone company-notifications__bone--mid" />
                </div>
                <span className="company-notifications__bone company-notifications__bone--row" />
                <div className="company-notifications__range">
                    <span className="company-notifications__bone company-notifications__bone--tile" />
                    <span className="company-notifications__bone company-notifications__bone--tile" />
                </div>
            </div>
        );
    }

    return (
        <div className="company-notifications">
            {strings.company_notification_schedule_desc ? (
                <p className="company-notifications__desc">{strings.company_notification_schedule_desc}</p>
            ) : null}

            <div className="company-notifications__card">
                <label className="company-notifications__row">
                    <span className="material-symbols-outlined company-notifications__glyph" aria-hidden="true">
                        notifications_active
                    </span>
                    <span className="company-notifications__row-label">{strings.allow_notifications}</span>
                    <span className="company-notifications__select-wrap">
                        <select
                            className="company-notifications__select"
                            value={notificationType}
                            onChange={(e) => setNotificationType(e.target.value)}
                        >
                            <option value="everyday">{strings.every_day}</option>
                            <option value="weekdays">{strings.weekdays}</option>
                            <option value="custom">{strings.custom}</option>
                        </select>
                        <span className="material-symbols-outlined company-notifications__select-chevron" aria-hidden="true">
                            expand_more
                        </span>
                    </span>
                </label>
            </div>

            {ranged ? (
                <div className="company-notifications__range">
                    <TimeTile
                        icon="bedtime"
                        label={startLabel}
                        value={startTime}
                        options={timeOptions}
                        onChange={(value) => handleTimeChange('startTime', value)}
                    />
                    <TimeTile
                        icon="wb_sunny"
                        label={endLabel}
                        value={endTime}
                        options={timeOptions}
                        onChange={(value) => handleTimeChange('endTime', value)}
                    />
                </div>
            ) : null}

            {notificationType === 'custom' ? (
                <div className="company-notifications__card">
                    {DAYS_OF_WEEK.map((day) => {
                        const preference = customPreferences[day.key];
                        const enabled = !!preference;
                        const dayLabel = getDayLabel(day.key);
                        return (
                            <div key={day.key} className="company-notifications__day">
                                <label className={`company-notifications__row${enabled ? '' : ' is-off'}`}>
                                    <span className="material-symbols-outlined company-notifications__glyph" aria-hidden="true">
                                        event
                                    </span>
                                    <span className="company-notifications__row-label">{dayLabel}</span>
                                    <span className="modern-toggle-switch modern-toggle-orange company-notifications__switch">
                                        <input
                                            type="checkbox"
                                            checked={enabled}
                                            aria-label={dayLabel}
                                            onChange={() => toggleDayEnabled(day.key)}
                                        />
                                        <span className="modern-toggle-slider"></span>
                                    </span>
                                </label>
                                {enabled ? (
                                    <div className="company-notifications__range company-notifications__range--day">
                                        <TimeTile
                                            icon="bedtime"
                                            label={startLabel}
                                            value={preference.startTime}
                                            options={timeOptions}
                                            onChange={(value) => updateCustomPreference(day.key, 'startTime', value)}
                                        />
                                        <TimeTile
                                            icon="wb_sunny"
                                            label={endLabel}
                                            value={preference.endTime}
                                            options={timeOptions}
                                            onChange={(value) => updateCustomPreference(day.key, 'endTime', value)}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            <button
                type="button"
                className={`kj-btn kj-btn--primary kj-btn--block${loading ? ' kj-btn--loading' : ''}`}
                onClick={saveNotificationPreferences}
                disabled={loading}
            >
                {loading ? strings.saving : strings.update}
            </button>
        </div>
    );
}

export default CompanyNotificationSettings;
